import { useState, useCallback, useEffect } from "react";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import { Button } from "@mui/material";
import { loadScript } from "utils/common";
import { renewSubscription, userSelector } from "store/features/authSlice";
import Logomain from "assets/images/creware.png";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { PDFDownloadLink } from "@react-pdf/renderer";
import MyDoc from "containers/pdf/Invoice/invoice";
import Createable from "containers/Select";
import { fetchSubscriptionPlans } from "services/auth";
import { fetchUSDExchangeRate } from "containers/pdf/utils";

function SubscriptionCard({
  title,
  description,
  info,
  referrals,
  personal,
  uid,
}) {
  const dispatch = useDispatch();
  const user = useSelector(userSelector);

  const [loader, setLoader] = useState(false);
  const [subscriptionTypev1, setSubscriptionTypev1] = useState([]);
  const [subscription, setSubscription] = useState();
  const [usdRate, setUsdRate] = useState(0.012); // mock or pass this dynamically
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  // Block menu open briefly after mount so the click that navigated here
  // (navbar name / subscription banner) cannot land on this select.
  const [allowMenuOpen, setAllowMenuOpen] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setAllowMenuOpen(true), 400);
    return () => window.clearTimeout(timer);
  }, []);

  const registerHandler = async (payResponse, referralList) => {
    setLoader(true);
    await dispatch(
      renewSubscription({ payResponse, uid, referralList, subscription })
    );
    setLoader(false);
  };

  useEffect(() => {
    const getExchangeRate = async () => {
      try {
        const dollarValue = await fetchUSDExchangeRate();
        setUsdRate(dollarValue);
      } catch (error) {
        console.error("Failed to fetch USD exchange rate:", error);
      }
    };

    getExchangeRate();
  }, []);

  const displayRazorpay = async () => {
    if (!subscription || !subscription.paymentAmount) {
      toast.error("Select subscription plan");
      return;
    }

    const GST_PERCENTAGE = subscription.gstPercentage || 18;
    const baseAmount = parseFloat(subscription.paymentAmount);
    const gstAmount = baseAmount + (baseAmount * GST_PERCENTAGE) / 100;

    let referAmount = 0;
    let referralList = referrals?.map((item) => {
      if (!item.redeemed) {
        if (referAmount !== gstAmount) {
          referAmount += item.amount;
          if (referAmount > gstAmount) {
            let amt = { amount: referAmount - gstAmount, ...item };
            referAmount = gstAmount;
            return amt;
          }
          return { ...item, redeemed: true };
        }
      }
      return item;
    });

    let finalPayableAmount = gstAmount - referAmount;
    const amount = finalPayableAmount <= 0 ? 1 * 100 : finalPayableAmount * 100;

    const res = await loadScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!res) {
      toast.error("Razorpay SDK failed to load, retry");
      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: amount,
      currency: "INR",
      name: "Fitify",
      description: "Subscription renew transaction",
      image: Logomain,
      prefill: {
        name: personal.name,
        email: personal.email,
        contact: personal.phoneNumber,
      },
      handler: function (response) {
        response = {
          ...response,
          subscriptionAmount: gstAmount,
          amount: amount / 100,
          referAmount: parseFloat(referAmount),
        };
        registerHandler(response, referralList ?? []);
      },
    };

    const paymentObj = new window.Razorpay(options);
    paymentObj.open();
  };

  const fetchSubscriptions = useCallback(async () => {
    try {
      let subs = [];
      subs = await fetchSubscriptionPlans();
      const formattedSubs = subs.map((plan) => {
        const gstPercentage = plan.gstPercentage ?? 0;
        const gstAmount = ((plan.paymentAmount * gstPercentage) / 100).toFixed(
          2
        );
        const totalAmount = (
          plan.paymentAmount + parseFloat(gstAmount)
        ).toFixed(2);
        const usdAmount = usdRate
          ? (plan.paymentAmount * usdRate).toFixed(2)
          : "0.00";

        return {
          ...plan,
          label: `${plan.plan} (${plan.months} month${
            plan.months > 1 ? "s" : ""
          }, $${usdAmount} ≈ ₹${plan.paymentAmount} + ₹${gstAmount} GST)`,
          gstAmount: parseFloat(gstAmount),
          totalPayableINR: parseFloat(totalAmount),
        };
      });
      setSubscriptionTypev1(formattedSubs);
    } catch (error) {
      toast.error("Failed to load subscription plans");
    }
  }, [usdRate]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const {
    nextRenewableDate,
    amountPaid,
    subscriptionType,
    dateOfSubscription,
  } = info;

  const onSelectSubscrition = (value) => {
    setSubscription(value);
  };

  const renderCard = () => (
    <SuiBox
      p={2}
      style={{
        border: "1px solid #D2D6DA",
        boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.05)",
        borderRadius: "16px",
      }}
    >
      {[
        ["Subscription Type", subscriptionType],
        ["Amount Paid", amountPaid],
        ["Date of Subscription", dateOfSubscription],
        ["Next Renewal Date", nextRenewableDate],
      ].map(([label, value]) => (
        <SuiBox display="flex" py={1} key={label}>
          <SuiTypography
            variant="button"
            fontWeight="bold"
            style={{ width: "60%" }}
          >
            {label}: &nbsp;
          </SuiTypography>
          <SuiTypography variant="button" fontWeight="regular" color="text">
            &nbsp;{value}
          </SuiTypography>
        </SuiBox>
      ))}

      <SuiBox display="flex" py={1}>
        <SuiTypography
          variant="button"
          fontWeight="bold"
          textTransform="capitalize"
          style={{ width: "60%" }}
        >
          Download Invoice: &nbsp;
        </SuiTypography>
        <PDFDownloadLink
          style={{ marginTop: -7 }}
          document={<MyDoc user={user} subscription={info} />}
          fileName="invoice.pdf"
        >
          {({ loading }) => (
            <SuiTypography
              style={{ color: "#17C1E8", textDecoration: "underline" }}
              variant="button"
              fontWeight="regular"
              color="text"
            >
              {loading ? "Loading..." : "Download"}
            </SuiTypography>
          )}
        </PDFDownloadLink>
      </SuiBox>
    </SuiBox>
  );

  return (
    <Card sx={{ height: "100%" }} style={{ overflow: "inherit" }}>
      <SuiBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pt={2}
        px={2}
      >
        <SuiTypography
          variant="h6"
          fontWeight="medium"
          textTransform="capitalize"
        >
          {title}
        </SuiTypography>
      </SuiBox>

      <SuiBox p={2}>
        <SuiBox mb={2} lineHeight={1}>
          <SuiTypography variant="button" color="text" fontWeight="regular">
            {description}
          </SuiTypography>
        </SuiBox>

        <SuiBox opacity={0.3}>
          <Divider />
        </SuiBox>

        <SuiBox>{renderCard()}</SuiBox>

        <SuiBox mt={2}>
          <Createable
            options={subscriptionTypev1 ?? []}
            autoComplete="new-password"
            name="profile-subscription-plan"
            inputId="profile-subscription-plan"
            isSearchable={false}
            menuPortalTarget={null}
            menuIsOpen={menuIsOpen}
            openMenuOnFocus={false}
            onMenuOpen={() => {
              if (allowMenuOpen) setMenuIsOpen(true);
            }}
            onMenuClose={() => setMenuIsOpen(false)}
            value={subscription}
            placeholder="Choose next Subscription plan"
            style={{ borderRadius: "0.5rem", fontSize: 14 }}
            inputStyle={{
              width: 190,
              height: "1.8rem",
              borderRadius: "0.5rem",
            }}
            onChange={onSelectSubscrition}
          />
        </SuiBox>

        <SuiBox mt={4} display="flex" justifyContent="center">
          <SuiButton
            style={{ width: "60%" }}
            component={Button}
            color="dark"
            variant="gradient"
            onClick={displayRazorpay}
            disabled={loader}
            fullWidth
          >
            {loader ? "Renewing . . ." : "Pay & Renew"}
          </SuiButton>
        </SuiBox>
      </SuiBox>
    </Card>
  );
}

export default SubscriptionCard;
